import React, { useState, useEffect } from 'react';
import SearchableDropdown from '../../../shared/components/ui/SearchableDropdown';
import { supabase } from '../../../shared/lib/supabase';

interface KeyEvent {
  id: string;
  match_id: string;
  team_id: string | null;
  stat_type_id: string;
  match_minute: number | null; // Made nullable until timer issue is fixed
  primary_player_id: string | null;
  secondary_player_id: string | null;
  tags: any[];
  status: 'published' | 'unpublished';
  published_at: string | null;
  published_by: string | null;
  source_log_id: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  event_timestamp: string | null;
  is_key_event?: boolean; // For timeline events - indicates if this is also a key event
  team?: {
    id: string;
    name: string;
    logo?: string;
  } | null;
  primary_player?: {
    id: string;
    full_name: string;
    kit_number?: number;
  } | null;
  secondary_player?: {
    id: string;
    full_name: string;
    kit_number?: number;
  } | null;
  stat_type?: {
    id: string;
    stat_key: string;
    display_name: string;
    category: string;
    event_result: string;
  } | null;
}

interface Player {
  id: string;
  full_name: string;
  kit_number?: number;
  team_id?: string;
  position?: string;
}

interface Team {
  id: string;
  name: string;
  logo?: string;
}

interface StatType {
  id: string;
  stat_key: string;
  display_name: string;
  emoji?: string;
  category?: string;
  event_result?: string;
  short_description?: string;
}

interface EditKeyEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: KeyEvent | null; // Allow null for create mode
  players: Player[];
  teams: Team[];
  statTypes: StatType[];
  onSave: (updatedEvent: Partial<KeyEvent> & { is_key_event?: boolean }) => Promise<void>;
  isViewOnly?: boolean;
  isCreateMode?: boolean;
  viewMode?: 'keyEvents' | 'allEvents'; // Track which view we're in
}

const EditKeyEventModal: React.FC<EditKeyEventModalProps> = ({
  isOpen,
  onClose,
  event,
  players,
  teams,
  statTypes,
  onSave,
  isViewOnly = false,
  isCreateMode = false,
  viewMode = 'keyEvents'
}) => {
  const [formData, setFormData] = useState({
    match_minute: null as number | null,
    team_id: '',
    primary_player_id: '',
    secondary_player_id: '',
    status: 'unpublished' as 'published' | 'unpublished',
    stat_type_id: '',
    is_key_event: false // New field for timeline events
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Show all players from match lineup (no team filtering needed)
  const filteredPlayers = React.useMemo(() => {
    console.log('EditKeyEventModal - Players received:', players);
    console.log('EditKeyEventModal - Filtered players:', players);
    return players;
  }, [players]);

  useEffect(() => {
    if (isOpen) {
      setErrors({}); // Reset errors when modal opens
      if (isCreateMode) {
        setFormData({
          match_minute: null,
          team_id: '',
          primary_player_id: '',
          secondary_player_id: '',
          status: 'unpublished',
          stat_type_id: '',
          is_key_event: false
        });
      } else if (event) {
        // Fetch is_key_event from match_timeline if editing/viewing a timeline event
        const fetchIsKeyEvent = async () => {
          if (viewMode === 'allEvents' && event.id) {
            // Use event.is_key_event if available, otherwise fetch from DB
            if (event.is_key_event !== undefined) {
              setFormData({
                match_minute: event?.match_minute || null,
                team_id: event?.team_id || '',
                primary_player_id: event?.primary_player_id || '',
                secondary_player_id: event?.secondary_player_id || '',
                status: event?.status || 'unpublished',
                stat_type_id: event?.stat_type?.id || '',
                is_key_event: event.is_key_event
              });
            } else {
              try {
                const { data: timelineEvent } = await supabase
                  .from('match_timeline')
                  .select('is_key_event')
                  .eq('id', event.id)
                  .single();

                setFormData({
                  match_minute: event?.match_minute || null,
                  team_id: event?.team_id || '',
                  primary_player_id: event?.primary_player_id || '',
                  secondary_player_id: event?.secondary_player_id || '',
                  status: event?.status || 'unpublished',
                  stat_type_id: event?.stat_type?.id || '',
                  is_key_event: timelineEvent?.is_key_event || false
                });
              } catch (error) {
                console.error('Error fetching is_key_event:', error);
                setFormData({
                  match_minute: event?.match_minute || null,
                  team_id: event?.team_id || '',
                  primary_player_id: event?.primary_player_id || '',
                  secondary_player_id: event?.secondary_player_id || '',
                  status: event?.status || 'unpublished',
                  stat_type_id: event?.stat_type?.id || '',
                  is_key_event: false
                });
              }
            }
          } else {
            // For key events, is_key_event is always true
            setFormData({
              match_minute: event?.match_minute || null,
              team_id: event?.team_id || '',
              primary_player_id: event?.primary_player_id || '',
              secondary_player_id: event?.secondary_player_id || '',
              status: event?.status || 'unpublished',
              stat_type_id: event?.stat_type?.id || '',
              is_key_event: viewMode === 'keyEvents' ? true : (event.is_key_event || false)
            });
          }
        };

        fetchIsKeyEvent();
      }
    }
  }, [isOpen, event, isCreateMode, viewMode]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      
      // Clear secondary_player_id when is_key_event is unchecked
      if (field === 'is_key_event' && value === false) {
        updated.secondary_player_id = '';
      }
      
      return updated;
    });
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const newErrors: Record<string, string> = {};
    
    if (isCreateMode) {
      if (!formData.stat_type_id) {
        newErrors.stat_type_id = 'Event type is required';
      }
      if (!formData.team_id) {
        newErrors.team_id = 'Team is required';
      }
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setErrors({});
    setLoading(true);

    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error saving event:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              {isCreateMode ? (
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
              ) : isViewOnly ? (
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              ) : (
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              )}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {isCreateMode 
                  ? 'Create Timeline Event' 
                  : isViewOnly 
                    ? viewMode === 'allEvents' ? 'View Timeline Event' : 'View Key Event'
                    : viewMode === 'allEvents' ? 'Edit Timeline Event' : 'Edit Key Event'}
              </h3>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4" noValidate>
          {/* 1. Event Type - First option */}
          {isCreateMode ? (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Event Type *
              </label>
              <SearchableDropdown
                value={formData.stat_type_id}
                onChange={(value) => {
                  handleInputChange('stat_type_id', value);
                  if (errors.stat_type_id) {
                    setErrors(prev => ({ ...prev, stat_type_id: '' }));
                  }
                }}
                options={statTypes.map(stat => ({
                  id: stat.id,
                  name: `${stat.emoji || '⚽'} ${stat.short_description}`
                }))}
                placeholder="Select Event Type"
                className="w-full"
              />
              {errors.stat_type_id && (
                <p className="text-xs text-red-600 mt-1">{errors.stat_type_id}</p>
              )}
            </div>
          ) : (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Event Type
              </label>
              <div className="text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded px-3 py-2">
                {(event?.stat_type as any)?.emoji ? `${(event?.stat_type as any).emoji} ` : ''}{event?.stat_type?.display_name || 'Unknown Event'}
              </div>
            </div>
          )}

          {/* 2. Team Selection - Second option */}
          {isCreateMode ? (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Team *
              </label>
              <SearchableDropdown
                value={formData.team_id}
                onChange={(value) => {
                  handleInputChange('team_id', value);
                  // Clear player selections when team changes
                  handleInputChange('primary_player_id', '');
                  handleInputChange('secondary_player_id', '');
                  if (errors.team_id) {
                    setErrors(prev => ({ ...prev, team_id: '' }));
                  }
                }}
                options={teams.map(team => ({
                  id: team.id,
                  name: team.name,
                  logo: team.logo
                }))}
                placeholder="Select Team"
                className="w-full"
              />
              {errors.team_id && (
                <p className="text-xs text-red-600 mt-1">{errors.team_id}</p>
              )}
            </div>
          ) : (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Team
              </label>
              <div className="text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded px-3 py-2">
                {event?.team?.name || 'No team assigned'}
              </div>
            </div>
          )}

          {/* Match Minute */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Match Minute
            </label>
            <input
              type="text"
              value={formData.match_minute || ''}
              onChange={(e) => handleInputChange('match_minute', e.target.value ? parseInt(e.target.value) : null)}
              disabled={isViewOnly}
              className={`w-full text-sm text-gray-900 border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                isViewOnly ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
              }`}
              placeholder="Enter minute (e.g., 45)"
            />
          </div>

          {/* Primary Player */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Primary Player
            </label>
            {isViewOnly ? (
              <div className="text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded px-3 py-2">
                {event?.primary_player?.full_name ? 
                  `${event?.primary_player?.full_name} ${event?.primary_player?.kit_number ? `#${event?.primary_player?.kit_number}` : ''}` : 
                  'No player selected'
                }
              </div>
            ) : (
              <SearchableDropdown
                value={formData.primary_player_id}
                onChange={(value) => handleInputChange('primary_player_id', value)}
                options={filteredPlayers.map(player => ({
                  id: player.id,
                  name: `${player.full_name} ${player.kit_number ? `#${player.kit_number}` : ''}`
                }))}
                placeholder="Select Primary Player"
                className="w-full"
                loading={false}
              />
            )}
          </div>

          {/* Secondary Player */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Secondary Player
            </label>
            {isViewOnly ? (
              <div className="text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded px-3 py-2">
                {event?.secondary_player?.full_name ? 
                  `${event?.secondary_player?.full_name} ${event?.secondary_player?.kit_number ? `#${event?.secondary_player?.kit_number}` : ''}` : 
                  'No player selected'
                }
              </div>
            ) : (
              <SearchableDropdown
                value={formData.secondary_player_id}
                onChange={(value) => handleInputChange('secondary_player_id', value)}
                options={filteredPlayers.map(player => ({
                  id: player.id,
                  name: `${player.full_name} ${player.kit_number ? `#${player.kit_number}` : ''}`
                }))}
                placeholder="Select Secondary Player"
                className="w-full"
                loading={false}
                disabled={!formData.is_key_event && (isCreateMode || viewMode === 'allEvents')}
              />
            )}
          </div>

          {/* Status */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Status
            </label>
            <div className="flex items-center space-x-3">
              <span className={`text-sm font-medium ${formData.status === 'published' ? 'text-gray-500' : 'text-gray-900'}`}>
                Unpublished
              </span>
              <button
                type="button"
                onClick={() => {
                  if (!isViewOnly) {
                    handleInputChange('status', formData.status === 'published' ? 'unpublished' : 'published');
                  }
                }}
                disabled={isViewOnly}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-green-300 focus:ring-offset-2 ${
                  formData.status === 'published' 
                    ? 'bg-green-600' 
                    : 'bg-gray-300'
                } ${isViewOnly ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform duration-200 ${
                    formData.status === 'published' ? 'translate-x-5' : 'translate-x-0.5'
                  }`}
                />
              </button>
              <span className={`text-sm font-medium ${formData.status === 'published' ? 'text-gray-900' : 'text-gray-500'}`}>
                Published
              </span>
            </div>
          </div>

          {/* Is Key Event - Show in create mode, edit mode for timeline events, or view mode for timeline events */}
          {(isCreateMode || (viewMode === 'allEvents')) && (
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.is_key_event}
                  onChange={(e) => handleInputChange('is_key_event', e.target.checked)}
                  disabled={isViewOnly}
                  className={`w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 ${
                    isViewOnly ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                  }`}
                />
                <span className="text-xs font-medium text-gray-700">
                  Is Key Event
                </span>
              </label>
              {!isViewOnly && (
                <p className="text-xs text-gray-500 mt-1 ml-6">
                  {isCreateMode 
                    ? 'If checked, this event will also be added to Key Match Events'
                    : 'If checked, this event will also appear in Key Match Events'}
                </p>
              )}
            </div>
          )}


          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
            >
              {isViewOnly ? 'Close' : 'Cancel'}
            </button>
            {!isViewOnly && (
              <button
                type="submit"
                disabled={loading}
                className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (isCreateMode ? 'Creating...' : 'Saving...') : (isCreateMode ? 'Create Event' : 'Save Changes')}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditKeyEventModal;
